import React, { useState, useEffect } from "react";
import { RequireStaff } from "@/components/RequireStaff";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Loader2, GraduationCap, Star, MapPin, Mail, Phone, DollarSign } from "lucide-react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
 
const expertT = {
  en: {
    title: "Our Experts", subtitle: "Internal and external experts available on the AcquizGroup platform.",
    search: "Search experts...", noExperts: "No experts found", rate: "/hr", projects: "projects",
  },
  fr: {
    title: "Nos experts", subtitle: "Experts internes et externes disponibles sur la plateforme AcquizGroup.",
    search: "Rechercher un expert...", noExperts: "Aucun expert trouvé", rate: "/h", projects: "projets",
  },
};
 
const ROLE_TABS = [
  { key: "all", en: "All", fr: "Tous" },
  { key: "business_law", en: "Business Lawyers", fr: "Avocats droit des affaires", match: "business" },
  { key: "immigration", en: "Immigration Lawyers", fr: "Avocats immigration", match: "immigration" },
  { key: "notary", en: "Notaries", fr: "Notaires", match: "notar" },
  { key: "tax", en: "Tax Advisors", fr: "Conseillers fiscaux", match: "tax|fiscal" },
  { key: "cpa", en: "CPA", fr: "CPA", match: "cpa|comptab" },
];
 
export default function StaffExperts() {
  const { language } = useLanguage();
  const labels = expertT[language] || expertT.en;
  const [consultants, setConsultants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeRole, setActiveRole] = useState("all");
 
  useEffect(() => {
    const fetchExperts = async () => {
      setLoading(true);
      try {
        // Remplace : supabase.from('consultants').select('*').order('rating', { ascending: false })
        const snap = await getDocs(query(collection(db, 'consultants'), orderBy('rating', 'desc')));
        setConsultants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading experts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchExperts();
  }, []);
 
  const filtered = consultants.filter((c) => {
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || c.name?.toLowerCase().includes(q) || c.specialty?.toLowerCase().includes(q) ||
      c.expertise?.some((e: string) => e.toLowerCase().includes(q));
    if (activeRole === "all") return matchesSearch;
    const tab = ROLE_TABS.find(t => t.key === activeRole);
    if (!tab?.match) return matchesSearch;
    const regex = new RegExp(tab.match, "i");
    return matchesSearch && (regex.test(c.specialty || "") || c.expertise?.some((e: string) => regex.test(e)));
  });
 
  return (
    <RequireStaff>
      <div className="space-y-4 font-['Geist',sans-serif]">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2"><GraduationCap className="w-5 h-5 text-amber-400" /> {labels.title}</h1>
            <p className="text-sm text-gray-400 mt-1">{labels.subtitle}</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input placeholder={labels.search} value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
          </div>
        </header>
 
        <div className="flex flex-wrap gap-1.5">
          {ROLE_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setActiveRole(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeRole === tab.key ? "bg-amber-500 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5"}`}>
              {language === "fr" ? tab.fr : tab.en}
            </button>
          ))}
        </div>
 
        <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
          ) : (
            <ScrollArea className="max-h-[calc(100vh-300px)]">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {filtered.map((c) => (
                  <div key={c.id} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-amber-500/30 transition-all">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-white text-sm">{c.name}</h3>
                      {c.rating > 0 && <span className="flex items-center gap-1 text-amber-400 text-xs"><Star className="w-3 h-3 fill-amber-400" /> {c.rating}</span>}
                    </div>
                    <p className="text-xs text-gray-300 mb-1">{c.specialty}</p>
                    {c.expertise?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {c.expertise.slice(0, 3).map((e: string) => <Badge key={e} variant="outline" className="text-[10px] border-white/15 text-gray-400">{e}</Badge>)}
                      </div>
                    )}
                    <div className="space-y-1 text-xs text-gray-500">
                      {c.location && <p className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</p>}
                      {c.email && <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</p>}
                      {c.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</p>}
                      {c.hourly_rate && <p className="flex items-center gap-1 text-amber-400/70"><DollarSign className="w-3 h-3" />{c.hourly_rate} {c.currency}{labels.rate}</p>}
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/5">
                      <Badge variant="outline" className={`text-[10px] ${c.availability === "available" ? "text-emerald-400 border-emerald-500/30" : "text-gray-400 border-white/10"}`}>{c.availability || "—"}</Badge>
                      {c.projects_completed > 0 && <span className="text-xs text-gray-500">{c.projects_completed} {labels.projects}</span>}
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <div className="col-span-full text-center py-8 text-gray-500">{labels.noExperts}</div>}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </RequireStaff>
  );
}
 